import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  UserCheck,
  Mail,
  Loader,
  Plus,
  Building2,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
} from 'lucide-react';
import {
  getAllDoctors,
  deleteDoctor,
  blockDoctor,
  unblockDoctor,
} from '../services/doctorService';
import { toast } from 'react-toastify';
import { DoctorProfile } from '../types';
import Modal from '../components/Common/Modal';
import CustomSelect from '../components/Common/CustomSelect';
import { paginationPageWindow } from '../utils/pagination';

const ITEMS_PER_PAGE = 10;

type FilterState = {
  status: string;
  onboardingStatus: string;
  department: string;
  hospital: string;
};

const Doctors: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    status: '',
    onboardingStatus: '',
    department: '',
    hospital: '',
  });

  const [doctorToDelete, setDoctorToDelete] = useState<DoctorProfile | null>(null);
  const [doctorToBlock, setDoctorToBlock] = useState<DoctorProfile | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorProfile | null>(null);
  const [doctors, setDoctors] = useState<DoctorProfile[]>([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);

  const fetchDoctorsFromAPI = async () => {
    setIsLoadingDoctors(true);

    try {
      const doctorsList = await getAllDoctors();
      setDoctors(doctorsList);
    } catch (error: any) {
      console.error("Failed to fetch doctors:", error);
      
      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response?.data?.message || error.message;
        
        if (status === 404) {
          toast.error("Doctors endpoint not found. Please ensure the backend API is running.");
        } else if (status === 403) {
          toast.error("You don't have permission to access doctor management.");
        } else if (status === 401) {
          toast.error("Authentication required. Please login again.");
        } else {
          toast.error(errorMessage || `Failed to load doctors (Status: ${status})`);
        }
      } else if (error.request) {
        toast.error("Unable to connect to server. Please check your connection.");
      } else {
        toast.error(error.message || "Failed to load doctors from server");
      }
      
      setDoctors([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  useEffect(() => {
    fetchDoctorsFromAPI();

    // Auto-refetch when window regains focus to reflect doctor edits immediately
    const handleFocus = () => {
      fetchDoctorsFromAPI();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const filteredDoctors = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return doctors.filter((doctor) => {
      const name = doctor.name?.toLowerCase() || '';
      const email = doctor.email?.toLowerCase() || '';
      const licenseNumber = (doctor.licenseNumber || doctor.medicalLicenseNumber || '').toLowerCase();
      const regNumber = (doctor.medicalRegistrationNumber || '').toLowerCase();
      const department = doctor.department?.toLowerCase() || '';
      const specialization = doctor.specialization?.toLowerCase() || '';
      const hospital = doctor.hospitalName?.toLowerCase() || '';

      const matchesSearch =
        name.includes(term) ||
        email.includes(term) ||
        licenseNumber.includes(term) ||
        regNumber.includes(term) ||
        department.includes(term) ||
        specialization.includes(term) ||
        hospital.includes(term);

      if (filters.status) {
        const isActive = filters.status === 'active';
        if (doctor.active !== isActive) return false;
      }

      if (filters.onboardingStatus && (doctor.onboardingStatus || 'not started') !== filters.onboardingStatus) {
        return false;
      }

      if (filters.department && (doctor.department || '') !== filters.department) {
        return false;
      }

      if (filters.hospital && (doctor.hospitalName || '') !== filters.hospital) {
        return false;
      }

      return matchesSearch;
    });
  }, [doctors, searchTerm, filters]);

  const totalPages = Math.ceil(filteredDoctors.length / ITEMS_PER_PAGE);
  const paginatedDoctors = filteredDoctors.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const clearFilters = () => {
    setFilters({ status: '', onboardingStatus: '', department: '', hospital: '' });
    setSearchTerm('');
  };

  const departmentOptions = useMemo(() => {
    const deps = Array.from(
      new Set(doctors.map((d) => d.department).filter((d): d is string => !!d && d !== ''))
    ).sort();
    return deps.map((d) => ({ value: d, label: d }));
  }, [doctors]);

  const hospitalOptions = useMemo(() => {
    const hosp = Array.from(
      new Set(doctors.map((d) => d.hospitalName).filter((h): h is string => !!h && h !== ''))
    ).sort();
    return hosp.map((h) => ({ value: h, label: h }));
  }, [doctors]);

  const handleDelete = async () => {
    if (!doctorToDelete) return;

    try {
      await deleteDoctor(doctorToDelete.userId);
      toast.success('Doctor deleted successfully!');
      setDoctors(prev => prev.filter(d => d.userId !== doctorToDelete.userId));
      setIsDeleteModalOpen(false);
      setDoctorToDelete(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred while deleting the doctor';
      toast.error(errorMessage);
      console.error('Delete error:', error);
    }
  };

  const handleBlockUnblock = async () => {
    if (!doctorToBlock) return;

    try {
      const isBlocking = doctorToBlock.active;
      const updatedDoctor = isBlocking 
        ? await blockDoctor(doctorToBlock.userId)
        : await unblockDoctor(doctorToBlock.userId);

      const action = isBlocking ? 'blocked' : 'unblocked';
      toast.success(`Doctor ${action} successfully!`);
      
      setDoctors(prev => prev.map(d => 
        d.userId === doctorToBlock.userId 
          ? updatedDoctor
          : d
      ));
      
      setIsBlockModalOpen(false);
      setDoctorToBlock(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || `An error occurred while ${doctorToBlock.active ? 'blocking' : 'unblocking'} the doctor`;
      toast.error(errorMessage);
      console.error('Block/Unblock error:', error);
    }
  };

  const handleCardClick = async (doctor: DoctorProfile) => {
    setSelectedDoctor(doctor);
    setIsProfileModalOpen(true);

    try {
      const freshDoctors = await getAllDoctors();
      const freshDoctor = freshDoctors.find((item) => item.userId === doctor.userId);
      setDoctors(freshDoctors);
      if (freshDoctor) setSelectedDoctor(freshDoctor);
    } catch (error) {
      console.error('Failed to refresh doctor details:', error);
    }
  };

  const handleEditClick = (doctor: DoctorProfile) => {
    navigate(`/doctors/${doctor.userId}/edit`);
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Doctor Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Doctor Onboarding</h1>
        </div>

        <button
          onClick={() => navigate('/doctors/new')}
          className="btn-primary flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
        >
          <Plus className="h-5 w-5" />
          <span>Add Doctor</span>
        </button>
      </div>

      {/* Search and Filter Card */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, department, hospital, specialization, or license/registration number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${hasActiveFilters ? 'ring-2 ring-primary-500' : ''}`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full font-medium">
                {Object.values(filters).filter((v) => v !== '').length}
              </span>
            )}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <CustomSelect
                  value={filters.status}
                  onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  placeholder="All Statuses"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Onboarding Status</label>
                <CustomSelect
                  value={filters.onboardingStatus}
                  onChange={(val) => setFilters((prev) => ({ ...prev, onboardingStatus: val }))}
                  options={[
                    { value: '', label: 'All Onboarding' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'in progress', label: 'In Progress' },
                    { value: 'not started', label: 'Not Started' },
                  ]}
                  placeholder="All Onboarding"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <CustomSelect
                  value={filters.department}
                  onChange={(val) => setFilters((prev) => ({ ...prev, department: val }))}
                  options={[{ value: '', label: 'All Departments' }, ...departmentOptions]}
                  placeholder="All Departments"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hospital</label>
                <CustomSelect
                  value={filters.hospital}
                  onChange={(val) => setFilters((prev) => ({ ...prev, hospital: val }))}
                  options={[{ value: '', label: 'All Hospitals' }, ...hospitalOptions]}
                  placeholder="All Hospitals"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex justify-end space-x-3 mt-4">
                <button onClick={clearFilters} className="btn-secondary text-sm flex items-center">
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoadingDoctors && (
        <div className="card text-center py-12">
          <Loader className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading doctors...</h3>
          <p className="text-gray-600">Please wait while we fetch the doctor records.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoadingDoctors && doctors.length === 0 && (
        <div className="card text-center py-16">
          <UserCheck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No doctors yet</h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
            There are currently no doctors onboarded on the platform.
          </p>
          <button
            onClick={() => navigate('/doctors/new')}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Onboard First Doctor</span>
          </button>
        </div>
      )}

      {/* No Search Results */}
      {!isLoadingDoctors && doctors.length > 0 && filteredDoctors.length === 0 && (
        <div className="card text-center py-16">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No doctors found</h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
            {hasActiveFilters
              ? 'No doctor records match your filters or search query. Try adjusting them.'
              : 'No doctor records match your search query. Try adjusting your search term.'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Doctors Table */}
      {!isLoadingDoctors && filteredDoctors.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Doctor
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Email
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Department
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Hospital
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Onboarding
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                    Last Updated
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedDoctors.map((doctor) => (
                  <tr key={doctor.userId} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserCheck className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4 min-w-0">
                          <button
                            onClick={() => handleCardClick(doctor)}
                            className="text-sm font-medium text-gray-900 truncate hover:text-primary-700 transition-colors"
                            title="View doctor details"
                          >
                            {doctor.name}
                          </button>
                          <div className="text-xs text-gray-500 sm:hidden truncate">{doctor.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{doctor.email}</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {doctor.department || 'General'}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <button
                        onClick={() => handleCardClick(doctor)}
                        className="flex items-center text-sm text-gray-600 hover:text-primary-700 transition-colors"
                        title="View doctor details"
                      >
                        <Building2 className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{doctor.hospitalName || 'N/A'}</span>
                      </button>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setDoctorToBlock(doctor);
                          setIsBlockModalOpen(true);
                        }}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          doctor.active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                        title={`Click to ${doctor.active ? 'deactivate' : 'activate'} doctor`}
                      >
                        {doctor.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          doctor.onboardingStatus === 'completed'
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                            : doctor.onboardingStatus === 'in progress'
                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                            : 'bg-amber-100 text-amber-800 border border-amber-200'
                        }`}
                      >
                        {doctor.onboardingStatus || 'not started'}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-xs text-gray-500 hidden xl:table-cell">
                      {doctor.updatedAt ? new Date(doctor.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-4">
                        <button
                          onClick={() => handleEditClick(doctor)}
                          className="text-primary-600 hover:text-primary-900 transition-colors duration-200"
                          title="Edit doctor"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setDoctorToDelete(doctor);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                          title="Delete doctor"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredDoctors.length)} of{' '}
                {filteredDoctors.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="flex items-center gap-1">
                  {paginationPageWindow(currentPage, totalPages).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`h-8 w-8 rounded-lg text-sm font-medium border flex items-center justify-center transition-colors ${
                        currentPage === page
                          ? 'bg-primary-600 border-primary-600 text-white cursor-default'
                          : 'bg-white border-gray-200 text-gray-500 hover:bg-primary-50 hover:text-primary-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Profile Detail Modal */}
      <Modal
        title="Doctor Details"
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedDoctor(null);
        }}
        widthClassName="max-w-xl"
      >
        {selectedDoctor && (
          <div className="space-y-6">
            {/* Header Profile Info */}
            <div className="flex items-center space-x-4 border-b border-gray-200 pb-4">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-8 w-8 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedDoctor.name}</h2>
                <p className="text-sm text-primary-700 font-medium">{selectedDoctor.specialization || 'Doctor'}</p>
                <p className="text-xs text-gray-500">{selectedDoctor.department || 'General Department'}</p>
              </div>
            </div>

            {/* Grid of Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Email</span>
                <span className="text-gray-900">{selectedDoctor.email || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Phone Number</span>
                <span className="text-gray-900">{selectedDoctor.phoneNumber || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Hospital</span>
                <span className="text-gray-900 font-medium">{selectedDoctor.hospitalName || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Department</span>
                <span className="text-gray-900">{selectedDoctor.department || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Med Registration No.</span>
                <span className="text-gray-900 font-mono">{selectedDoctor.medicalRegistrationNumber || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">License Number</span>
                <span className="text-gray-900 font-mono">
                  {selectedDoctor.licenseNumber || selectedDoctor.medicalLicenseNumber || 'N/A'}
                </span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Qualification</span>
                <span className="text-gray-900">{selectedDoctor.qualification || 'N/A'}</span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Years of Experience</span>
                <span className="text-gray-900">
                  {selectedDoctor.yearsOfExperience !== null && selectedDoctor.yearsOfExperience !== undefined
                    ? `${selectedDoctor.yearsOfExperience} Years`
                    : 'N/A'}
                </span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Status</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mt-1 ${
                    selectedDoctor.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {selectedDoctor.active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div>
                <span className="font-semibold text-gray-600 block text-xs uppercase">Onboarding Status</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize mt-1 ${
                    selectedDoctor.onboardingStatus === 'completed'
                      ? 'bg-emerald-100 text-emerald-800'
                      : selectedDoctor.onboardingStatus === 'in progress'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {selectedDoctor.onboardingStatus || 'not started'}
                </span>
              </div>
            </div>

            {/* Edit Button */}
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={() => {
                  setIsProfileModalOpen(false);
                  setSelectedDoctor(null);
                  handleEditClick(selectedDoctor);
                }}
                className="w-full btn-primary text-sm flex items-center justify-center gap-2"
              >
                <Pencil className="h-4 w-4" />
                Edit Doctor Profile
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Block/Unblock Confirmation Modal */}
      <Modal
        title={doctorToBlock?.active ? 'Deactivate Doctor' : 'Activate Doctor'}
        isOpen={isBlockModalOpen && doctorToBlock !== null}
        onClose={() => {
          setIsBlockModalOpen(false);
          setDoctorToBlock(null);
        }}
        widthClassName="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to {doctorToBlock?.active ? 'deactivate' : 'activate'}{' '}
          <span className="font-semibold text-gray-900">{doctorToBlock?.name}</span>?
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setIsBlockModalOpen(false);
              setDoctorToBlock(null);
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleBlockUnblock}
            className={doctorToBlock?.active ? "btn-danger" : "btn-primary"}
          >
            {doctorToBlock?.active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Doctor"
        isOpen={isDeleteModalOpen && doctorToDelete !== null}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDoctorToDelete(null);
        }}
        widthClassName="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to delete{' '}
          <span className="font-semibold text-gray-900">{doctorToDelete?.name}</span>? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setIsDeleteModalOpen(false);
              setDoctorToDelete(null);
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="btn-danger"
          >
            Delete Doctor
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Doctors;
